/**
 * Standards Review Page - EA-Agile
 * Adapted from Tim-Combo ApprovalInboxPage
 *
 * Displays pending draft standards awaiting review from approval groups
 */

import React, { useCallback, useState } from 'react';
import { FiClipboard } from 'react-icons/fi';
import StandardReviewInbox from '../components/StandardReviewInbox';
import ReviewActionModal from '../components/ReviewActionModal';

export interface StandardReviewItem {
  review_id: string;
  standard_id: string;
  standard_title: string;
  standard_type: string;
  focus_category: string;
  client_id: string;
  reviewer_email: string;
  approval_group_id: string;
  status: 'pending' | 'approved' | 'rejected' | 'changes_requested';
  review_comment?: string;
  review_round: number;
  created_at: string;
  updated_at: string;
  // Linked incident data
  incident_card_id?: string;
  incident_title?: string;
  root_cause_summary?: string;
  proposed_solution?: string;
  ai_confidence_score?: number;
}

const StandardsReviewPage: React.FC = () => {
  const [selectedReview, setSelectedReview] = useState<StandardReviewItem | null>(null);
  const [allReviews, setAllReviews] = useState<StandardReviewItem[]>([]);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Handle review selection
  const handleReviewSelect = useCallback((review: StandardReviewItem) => {
    setSelectedReview(review);
    setIsDetailModalOpen(true);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedReview(null);
  }, []);

  // Handle review action completion (approve/reject/request changes)
  const handleActionComplete = useCallback(() => {
    // Trigger refresh of the inbox
    setRefreshTrigger(prev => prev + 1);

    // Close modal after brief delay to show success
    setTimeout(() => {
      setIsDetailModalOpen(false);
      setSelectedReview(null);
    }, 1500);
  }, []);

  // Navigate to next review
  const handleNavigateNext = useCallback(() => {
    if (!selectedReview || allReviews.length === 0) return;

    const currentIndex = allReviews.findIndex(r => r.review_id === selectedReview.review_id);
    if (currentIndex < allReviews.length - 1) {
      const nextReview = allReviews[currentIndex + 1];
      setSelectedReview(nextReview);
    }
  }, [selectedReview, allReviews]);

  // Navigate to previous review
  const handleNavigatePrevious = useCallback(() => {
    if (!selectedReview || allReviews.length === 0) return;

    const currentIndex = allReviews.findIndex(r => r.review_id === selectedReview.review_id);
    if (currentIndex > 0) {
      const previousReview = allReviews[currentIndex - 1];
      setSelectedReview(previousReview);
    }
  }, [selectedReview, allReviews]);

  // Check navigation availability
  const getNavigationState = () => {
    if (!selectedReview || allReviews.length === 0) {
      return { hasNext: false, hasPrevious: false };
    }

    const currentIndex = allReviews.findIndex(r => r.review_id === selectedReview.review_id);
    return {
      hasNext: currentIndex < allReviews.length - 1,
      hasPrevious: currentIndex > 0
    };
  };

  const { hasNext, hasPrevious } = getNavigationState();

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center space-x-3 mb-2">
          <FiClipboard className="h-8 w-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Standards Review</h1>
        </div>
        <p className="text-gray-600">
          Review draft standards generated from incidents and approve for publication
        </p>
      </div>

      {/* Standards Review Inbox */}
      <StandardReviewInbox
        key={refreshTrigger}
        onReviewSelect={handleReviewSelect}
        selectedReviewId={selectedReview?.review_id}
        onReviewsLoad={setAllReviews}
      />

      {/* Review Action Modal */}
      <ReviewActionModal
        isOpen={isDetailModalOpen}
        onClose={handleModalClose}
        review={selectedReview}
        onActionComplete={handleActionComplete}
        onNavigateNext={handleNavigateNext}
        onNavigatePrevious={handleNavigatePrevious}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
      />
    </div>
  );
};

export default StandardsReviewPage;
